import React from 'react';
import { Box, Typography } from '@mui/material';

export interface MetaFact {
  key: string;
  /** The fact itself, e.g. "3 widgets". Written as it should read. */
  label: string;
  /** Sits before the label, at the same size as the text. */
  icon?: React.ReactNode;
}

export interface MetaFactsProps {
  facts: MetaFact[];
  ariaLabel?: string;
}

/**
 * A line of small facts about what is on screen — "3 widgets · Updated 5m ago".
 *
 * The same idea as a result frame's `meta`, for a page rather than a card:
 * quiet, secondary, and separated by middots so it reads as one line rather
 * than a row of labels. Empty facts are dropped, so a caller can pass a
 * conditional without leaving a stray separator behind.
 *
 * For how current the data is, prefer `DataFreshness` — it keeps itself up to
 * date and warns when the data is stale, which a plain fact cannot.
 */
export function MetaFacts({ facts, ariaLabel }: MetaFactsProps) {
  const shown = facts.filter((f) => f.label);
  if (shown.length === 0) return null;

  return (
    <Box
      role="list"
      aria-label={ariaLabel}
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}
    >
      {shown.map((fact, i) => (
        <Box key={fact.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {i > 0 && (
            <Typography
              aria-hidden
              variant="caption"
              sx={(t) => ({ color: t.tokens.text.disabled, display: { xs: 'none', sm: 'block' } })}
            >
              ·
            </Typography>
          )}
          <Typography
            role="listitem"
            variant="caption"
            sx={(t) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              color: t.tokens.text.secondary,
              '& svg': { fontSize: t.typography.caption.fontSize, color: t.tokens.text.tertiary, flexShrink: 0 },
            })}
          >
            {fact.icon}
            {fact.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
